/**
 * useControlledState hook
 *
 * The purpose of this hook is to facilitate the management of controlled / uncontrolled form elements.
 * Elements that typically maintain their own state and update based on user input.
 * More info: https://fb.me/react-controlled-components
 *
 * The control is usually applied to the element's `value` or `checked` property.
 * If a parent controls the state of the component, then it should pass a `value` or `checked` prop. (controlled)
 * If the component should be uncontrolled, then no `value` or `checked` prop should be passed in.
 * And if a default value is needed these will be passed in as the defaultValue || defaultChecked attr.
 *
 * Example:
 * const [value, setValueState] = useControlledState({
 *    controlled: props.value,
 *    default: props.defaultValue,
 *    name: 'ComponentName',
 * });
 *
 * The one component can now easily behave as a controlled or uncontrolled
 * without added logic to behave as stateful or stateless.
 */

import React from 'react';
import { UseControlledStateProps } from './useControlledState.types';

const isProduction = process.env.NODE_ENV === 'production';

export default function useControlledState({ controlled, default: defaultProp, name }: UseControlledStateProps) {
  const { current: isControlled } = React.useRef(controlled !== undefined);
  const [valueState, setValue] = React.useState(defaultProp);
  const value = isControlled ? controlled : valueState;

  if (!isProduction) {
    React.useEffect(() => {
      if (isControlled !== (controlled !== undefined)) {
        console.error(
          [
            `A component is changing ${isControlled ? 'a ' : 'an un'}controlled ` +
              `${name} to be ${isControlled ? 'un' : ''}controlled.`,
            'Elements should not switch from uncontrolled to controlled (or vice versa).',
            `Decide between using a controlled or uncontrolled ${name} ` + 'element for the lifetime of the component.',
            'More info: https://fb.me/react-controlled-components',
          ].join('\n'),
        );
      }
    }, [controlled]);

    const { current: defaultValue } = React.useRef(defaultProp);

    React.useEffect(() => {
      if (defaultValue !== defaultProp) {
        console.error(
          [
            `A component is changing the default value of an uncontrolled ${name} after being initialized. `,
            `To suppress this warning opt to use a controlled ${name}.`,
          ].join('\n'),
        );
      }
    }, [JSON.stringify(defaultProp)]);
  }

  const setValueIfUncontrolled = React.useCallback((newValue: unknown) => {
    if (!isControlled) {
      setValue(newValue);
    }
  }, []);

  return [value, setValueIfUncontrolled];
}
